import { useEffect, useState } from 'react';

import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { login } from '@/lib/api/auth';
import { clearSession } from '@/lib/auth-session';
import { useAuthStore } from '@/store/authStore';

const loginSchema = z.object({
  username: z.string().min(1, '아이디를 입력해주세요'),
  password: z.string().min(8, '비밀번호는 8자 이상이어야 합니다'),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fromPath = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  useEffect(() => {
    if (accessToken) {
      navigate(fromPath, { replace: true });
    }
  }, [accessToken, fromPath, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = async (values: LoginForm): Promise<void> => {
    setSubmitError(null);
    try {
      const data = await login(values.username, values.password);
      // 로그아웃을 거치지 않고 끝난 세션(탭 복원, 토큰 만료 등)의 캐시가 남아 있을
      // 수 있다. 새 계정 정보를 넣기 직전에 한 번 더 비운다.
      clearSession();
      setAuth(data);
      navigate(fromPath, { replace: true });
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 401) {
        const message = (err.response.data as { message?: string })?.message;
        setSubmitError(message ?? '아이디 또는 비밀번호가 일치하지 않습니다');
      } else {
        setSubmitError('로그인 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ width: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3} component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={0.5}>
              <Typography variant="h5" fontWeight={700}>
                케어허브 BGT 어드민
              </Typography>
              <Typography variant="body2" color="text.secondary">
                직원 로그인
              </Typography>
            </Stack>

            <TextField
              label="아이디"
              autoComplete="username"
              autoFocus
              {...register('username')}
              error={!!errors.username}
              helperText={errors.username?.message}
            />

            <TextField
              label="비밀번호"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
            />

            {submitError && <Alert severity="error">{submitError}</Alert>}

            <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
              {isSubmitting ? '로그인 중...' : '로그인'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
